import React, { useState, useRef } from 'react';
import {
  Box,
  Modal,
  Typography,
  Button,
  TextField,
  IconButton,
  Paper,
  Stack,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import UploadIcon from '@mui/icons-material/Upload';
import { FileUploader } from 'react-drag-drop-files';
import DeleteIcon from '@mui/icons-material/Delete';
import { editVisualFiles, uploadVisualNotesFile } from '../../apis/apiList/projectApi';
import { useMessage } from '../../context/MessageContext';

const StyledModalBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '95%',
  maxWidth: 600,
  backgroundColor: 'white',
  borderRadius: 12,
  boxShadow: 24,
  padding: theme.spacing(3),
}));

const DropArea = styled(Box)(({ theme }) => ({
  border: '2px dashed #ccc',
  borderRadius: 8,
  backgroundColor: '#fafafa',
  padding: theme.spacing(4),
  textAlign: 'center',
  cursor: 'pointer',
  transition: '0.2s',
  '&:hover': {
    backgroundColor: '#f0f0f0',
    borderColor: theme.palette.primary.main,
  },
  position: 'relative',
  zIndex: 2,
}));

const FileItem = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1),
  minHeight: '28px',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  overflow: 'hidden',
  justifyContent: 'space-between',
}));

const VisualFileUploadModal = ({ open, onClose, userDetails, projectId, onSave, topicDetail, actionType, visualDetails }) => {
  const [note, setNote] = useState(actionType == 'edit' ? visualDetails.note : '');
  const [files, setFiles] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef();

  const { showMessage } = useMessage();

  const fileTypes = [
    'JPG',
    'PNG',
    'GIF',
    'JPEG',
    'BMP',
    'TIFF',
    'MP4',
    'WEBM',
    'MOV',
    'AVI',
    'FLV',
    'WMV',
  ];

  const handleFileDrop = (selectedFiles) => {
    setFiles((prev) => [...prev, ...selectedFiles]);
    setError('');
  };

  const openFileDialog = () => {
    fileInputRef.current.click();
  };

  const handleManualFileSelect = (e) => {
    const selected = Array.from(e.target.files);
    const invalidFiles = selected.filter(
      (file) =>
        !fileTypes.includes(file.name.split('.').pop().toUpperCase())
    );

    if (invalidFiles.length > 0) {
      setError(`Invalid file(s): ${invalidFiles.map((f) => f.name).join(', ')}`);
    } else {
      setFiles((prev) => [...prev, ...selected]);
      setError('');
    }
  };

  const handleTypeError = (fileType) => {
    setError(`Invalid file type: ${fileType}`);
  };

  const handleSave = async () => {
    // console.log('topicDetail', topicDetail);
    // console.log('note', note);
    // console.log('files', files);
    setSaving(true);
    const formData = new FormData();
    formData.append('topicDetail', JSON.stringify(topicDetail));
    formData.append('note', note);
    files.forEach((file) => {
      formData.append('files', file);
    });
    console.log('projectId', projectId);
    console.log('userDetails', userDetails);
    try {
      const response = await uploadVisualNotesFile(projectId, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "x-access-token": userDetails?.jwtToken
        },
      });
      console.log('response', response);
      if(!response.topicAlreadyExists){
        onSave(response);
      }
      showMessage(response.message, 'success');
      setNote('');
      setFiles([]);
      setError('');
      setSaving(false);
      onClose();
    } catch (error) {
      console.log('error', error);
      setSaving(false);
    }

  };

  const handleRemoveFile = (indexToRemove) => {
    setFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleVisualUpdate = async () => {
    console.log('updating', files);
    setSaving(true);
    const formData = new FormData();
    formData.append('topicDetail', JSON.stringify(topicDetail));
    formData.append('note', note);
    files.forEach((file) => {
      formData.append('files', file);
    });
    console.log('visualDetails._id', visualDetails._id);
    console.log('userDetails', userDetails);
    try {
      const response = await editVisualFiles(visualDetails._id, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "x-access-token": userDetails?.jwtToken
        },  
      });
      console.log('response', response);
      onSave(response.visualFile);
      showMessage(response.message, 'success');
      setFiles([]);
      setError('');
      setSaving(false);
      onClose();
    } catch (error) {
      console.log('error', error);
      setSaving(false);
    }

    // onSave();
    // onClose();
  }

  return (
    <Modal open={open} onClose={onClose}>
      <StyledModalBox>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h3">{ actionType == 'edit' ? 'Edit Visual Notes' : 'Add Visual Notes'}</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        
        <TextField
          label="Enter note"
          className="topic-text"
          multiline
          rows={3}
          fullWidth
          margin="normal"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />

        <FileUploader
          multiple={true}
          handleChange={handleFileDrop}
          onTypeError={handleTypeError}
          name="file"
          types={fileTypes}
          children={
            <DropArea >
              <UploadIcon sx={{ fontSize: 40, color: 'gray' }} />
              <Typography variant="body1" mt={1} textAlign='center'>
                Drag & drop or click to upload
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign='center'>
                Supported: Images, Videos
              </Typography>
            </DropArea>
          }
        />

        {/* Hidden fallback file input */}
        <input
          type="file"
          multiple
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept="image/*,video/*,audio/*"
          onChange={handleManualFileSelect}
        />

        {error && (
          <Typography variant="body2" color="error" mt={1}>
            {error}
          </Typography>
        )}

        {files.length > 0 && (
          <Box mt={2}>
            <Typography variant="subtitle2">Uploaded Files:</Typography>
            <Stack spacing={1} maxHeight={150} overflow="auto">
                {files.map((file, idx) => (
                    <FileItem variant="outlined" key={idx} >
                        <Box display="flex" alignItems="center">
                            <InsertDriveFileIcon />
                            <Typography variant="body2">
                                {file.name}
                            </Typography>
                        </Box>
                        <IconButton
                            size="small"
                            onClick={() => handleRemoveFile(idx)}
                            sx={{ color: 'red' }}
                            >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </FileItem>
                ))}
            </Stack>
          </Box>
        )}

        <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
          <Button onClick={onClose} color="secondary">
            Cancel
          </Button>
          {/* <Button onClick={()=>{actionType == 'upload' ? handleSave() : handleVisualUpdate()}} variant="contained" disabled={actionType == 'upload' && ( files.length == 0 || note == '' ) || saving}> */}
          <Button onClick={()=>{actionType == 'upload' ? handleSave() : handleVisualUpdate()}} variant="contained" disabled={actionType == 'upload' && ( note == '' ) || saving}>
            {
              actionType == 'upload' && (
                <>
                  { saving ? 'Adding...' : 'Add'}
                </>
              )
            }
            {
              actionType == 'edit' && (
                <>
                  { saving ? 'Saving...' : 'Save'}
                </>
              )
            }
          </Button>
        </Box>
      </StyledModalBox>
    </Modal>
  );
};

export default VisualFileUploadModal;
